import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import {
  useGetSystemGroupsQuery,
  useUpdateSystemGroupMutation,
  useDeleteSystemGroupMutation,
  useNotifyGroupMutation,
} from '../../api/groupApi';
import { useToast } from '../../context/ToastContext';
import { errMsg } from '../../utils/errMsg';
import StatusDots from '../../components/StatusDots/StatusDots';
import MessageField from '../../components/MessageField/MessageField';
import { messageMaxLengthForChannels } from '../../utils/messageLimits';
import buttons from '../../styles/shared/buttons.module.css';
import s from '../../styles/shared/page.module.css';
import table from '../../styles/shared/table.module.css';

export default function SystemNotificationsPage() {
  const navigate = useNavigate();
  const showToast = useToast();
  const [notifyGroupTarget, setNotifyGroupTarget] = useState(null);
  const [message, setMessage] = useState('');

  const {
    data: groups,
    isLoading: loadingGroups,
    isError: groupsError,
    error: groupsLoadError,
  } = useGetSystemGroupsQuery();

  const [updateGroup] = useUpdateSystemGroupMutation();
  const [deleteGroup] = useDeleteSystemGroupMutation();
  const [notifyGroup, { isLoading: isSendingNotification }] = useNotifyGroupMutation();

  useEffect(() => {
    if (groupsError)
      showToast('error', 'System notifications', errMsg(groupsLoadError, 'Could not load groups.'));
  }, [groupsError, groupsLoadError, showToast]);

  function openNotifyDialog(group) {
    setNotifyGroupTarget(group);
    setMessage('');
  }

  function goScheduleInstead() {
    navigate('/admin/schedule', { state: { groupId: notifyGroupTarget.id } });
  }

  async function handleSendNotification() {
    try {
      const res = await notifyGroup({ groupId: notifyGroupTarget.id, message: message.trim() }).unwrap();
      const a = res.audience;
      showToast(
        'success',
        'System notifications',
        `Notification sent — email: ${a.email}, text: ${a.text}, telegram: ${a.telegram}.`,
      );
      setNotifyGroupTarget(null);
    } catch (err) {
      showToast('error', 'System notifications', errMsg(err, 'Could not send notification.'));
    }
  }

  async function handleToggleEnabled(group) {
    try {
      await updateGroup({ id: group.id, enabled: !group.enabled }).unwrap();
      showToast('success', 'System notifications', group.enabled ? 'Group disabled.' : 'Group enabled.');
    } catch (err) {
      showToast('error', 'System notifications', errMsg(err, 'Could not update group.'));
    }
  }

  async function handleDelete(group) {
    try {
      await deleteGroup(group.id).unwrap();
      showToast('success', 'System notifications', 'Group deleted.');
    } catch (err) {
      showToast('error', 'System notifications', errMsg(err, 'Could not delete group.'));
    }
  }

  const goEdit = (group) => navigate(`/admin/system-groups/${group.id}/edit`);
  const goSubscribers = (group) => navigate(`/admin/system-groups/${group.id}/subscribers`);

  const actionBodyTemplate = (group) => (
    <div className={table.actions}>
      <Button icon="pi pi-pencil" text className={table.iconNeutral} onClick={() => goEdit(group)} />
      <Button
        icon="pi pi-users"
        text
        className={table.iconNeutral}
        onClick={() => goSubscribers(group)}
      />
      <Button
        icon="pi pi-megaphone"
        text
        className={table.iconNeutral}
        onClick={() => openNotifyDialog(group)}
      />
      <Button
        icon={`pi ${group.enabled ? 'pi-ban' : 'pi-check'}`}
        text
        className={group.enabled ? table.iconDisable : table.iconSuccess}
        onClick={() => handleToggleEnabled(group)}
      />
      <Button
        icon="pi pi-trash"
        text
        className={table.iconDanger}
        onClick={() => handleDelete(group)}
      />
    </div>
  );

  return (
    <div className={s.page}>
      <div className={s.header}>
        <div>
          <h1>System notifications</h1>
          <p className={`${s.subtitle} textMuted`}>
            Groups hidden from public and managers, used for system-level alerts. Subscribers are added manually.
          </p>
        </div>
        <div style={{ display: 'flex', gap: '12px' }}>
          <Button
            label="Schedule"
            icon="pi pi-calendar"
            outlined
            onClick={() => navigate('/admin/schedule')}
          />
          <Button
            label="Add system group"
            icon="pi pi-plus"
            className={buttons.btnPrimary}
            onClick={() => navigate('/admin/system-groups/create')}
          />
        </div>
      </div>

      <div className={table.tableWrapper}>
        <DataTable
          value={groups ?? []}
          loading={loadingGroups}
          emptyMessage="No system groups yet. Add one above."
          responsiveLayout="stack"
        >
          <Column
            field="name"
            header="Name"
            className={table.contactCol}
            body={(g) => <div className={table.cellInsider}>{g.name}</div>}
          />
          <Column
            header="Channels"
            className={`${s.mutedCell} ${table.contactCol}`}
            style={{ width: '200px' }}
            body={(g) => (
              <div className={table.cellInsider}>
                {(g.channels ?? []).filter((c) => c !== 'push').join(', ') || '—'}
              </div>
            )}
          />
          <Column
            header="Status"
            className={table.statusCol}
            style={{ width: '110px' }}
            body={(g) => (
              <div className={table.cellInsider}>
                <div className={`${table.statusBadgesDesktop} ${g.enabled ? s.statusOk : s.statusDisabled}`}>
                  {g.enabled ? 'enabled' : 'disabled'}
                </div>
                <StatusDots
                  items={[
                    {
                      label: g.enabled ? 'enabled' : 'disabled',
                      color: g.enabled ? 'var(--color-success)' : 'var(--color-error)',
                    },
                  ]}
                />
              </div>
            )}
          />
          <Column
            header="Actions"
            className={table.actionsCol}
            body={actionBodyTemplate}
            style={{ width: '406px' }}
          />
        </DataTable>
      </div>

      <Dialog
        header={`Send notification${notifyGroupTarget ? ` — ${notifyGroupTarget.name}` : ''}`}
        visible={Boolean(notifyGroupTarget)}
        onHide={() => setNotifyGroupTarget(null)}
        style={{ width: '520px' }}
        modal
      >
        <MessageField
          id="message"
          value={message}
          onChange={setMessage}
          maxLength={messageMaxLengthForChannels(notifyGroupTarget?.channels)}
        />
        <Button
          label="Send"
          className={buttons.saveBtn}
          loading={isSendingNotification}
          disabled={!message.trim()}
          onClick={handleSendNotification}
        />
        <Button
          label="Schedule for later instead"
          icon="pi pi-calendar"
          text
          className="w-full mt-1"
          onClick={goScheduleInstead}
        />
      </Dialog>
    </div>
  );
}
